import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchVacationsAsync,
  fetchVacationStatsAsync,
  deleteVacationAsync,
  clearError,
} from '../store/slices/vacationsSlice';
import LoadingSpinner from './LoadingSpinner';
import ConfirmationModal from './ConfirmationModal';
import { formatDateEuropean } from '../utils/dateUtils';
import './Students.css';
import './Dashboard.css';

const Vacations: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { vacations, stats, loading, error } = useAppSelector((state) => state.vacations);

  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; id: number | null }>({
    isOpen: false,
    id: null,
  });

  useEffect(() => {
    dispatch(fetchVacationsAsync());
    dispatch(fetchVacationStatsAsync());
  }, [dispatch]);

  const handleDelete = useCallback((id: number) => {
    setDeleteModal({ isOpen: true, id });
  }, []);

  const confirmDelete = useCallback(async () => {
    if (deleteModal.id) {
      await dispatch(deleteVacationAsync(deleteModal.id));
      dispatch(fetchVacationStatsAsync());
    }
    setDeleteModal({ isOpen: false, id: null });
  }, [dispatch, deleteModal.id]);

  const cancelDelete = useCallback(() => {
    setDeleteModal({ isOpen: false, id: null });
  }, []);

  if (loading && vacations.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Vacations</h1>
          <p>Manage your vacation and leave records</p>
        </div>
        <button
          onClick={() => navigate('/vacations/create')}
          className="btn-primary"
        >
          + Add Vacation
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      {stats && (
        <div className="dashboard-grid" style={{ marginBottom: '1.5rem' }}>
          <div className="dashboard-card">
            <h3>Vacation Days ({stats.year})</h3>
            <div className="card-stats">
              <span className="stat-number">{stats.vacationDaysUsed} / {stats.vacationDaysTotal}</span>
              <span className="stat-label">days used</span>
            </div>
            <div style={{
              marginTop: '0.5rem',
              height: '6px',
              background: '#e2e8f0',
              borderRadius: 'var(--radius)',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                width: `${Math.min(100, (stats.vacationDaysUsed / stats.vacationDaysTotal) * 100)}%`,
                background: stats.vacationDaysUsed > stats.vacationDaysTotal ? '#e53e3e' : '#667eea',
                borderRadius: 'var(--radius)',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>
          <div className="dashboard-card">
            <h3>Illness Days ({stats.year})</h3>
            <div className="card-stats">
              <span className="stat-number">{stats.illnessDaysUsed} / {stats.illnessDaysTotal}</span>
              <span className="stat-label">days used</span>
            </div>
            <div style={{
              marginTop: '0.5rem',
              height: '6px',
              background: '#e2e8f0',
              borderRadius: 'var(--radius)',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                width: `${Math.min(100, (stats.illnessDaysUsed / stats.illnessDaysTotal) * 100)}%`,
                background: stats.illnessDaysUsed > stats.illnessDaysTotal ? '#e53e3e' : '#48bb78',
                borderRadius: 'var(--radius)',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>
        </div>
      )}

      <div className="students-table-container vocabulary-table">
        {vacations.length === 0 ? (
          <div className="no-students">
            <p>No vacation records found.</p>
          </div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vacations.map((vacation) => (
                <tr key={vacation.id}>
                  <td>
                    <strong>{vacation.vacationType?.name || '—'}</strong>
                  </td>
                  <td>{formatDateEuropean(vacation.dateFrom)}</td>
                  <td>{formatDateEuropean(vacation.dateTo)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => navigate(`/vacations/edit/${vacation.id}`)}
                        className="btn-icon"
                        data-tooltip="Edit"
                      >
                        ✎
                      </button>
                      <button
                        onClick={() => handleDelete(vacation.id)}
                        className="btn-icon btn-danger"
                        data-tooltip="Delete"
                      >
                        🗑
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {loading && vacations.length > 0 && (
        <div className="loading-overlay">
          <LoadingSpinner />
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        title="Delete Vacation"
        message="Are you sure you want to delete this vacation record?"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default Vacations;
